import { PencilIcon, Plus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { Button } from '@/components/lemmala-design-system/button/button'
import { Input } from '@/components/lemmala-design-system/input/input'

type InlineEditorProps = {
  value?: string
  onChange: (value: string) => void
  // Enter or blur ends the edit with onSubmit, Escape with onCancel.
  onSubmit?: () => void
  onCancel?: () => void
  placeholder?: string
  className?: string
  maxLength?: number | null
  mode?: 'edit' | 'view'
  readOnly?: boolean
  variant?: 'borderless' | 'outline'
  required?: boolean
  // Expandable input props
  expandable?: boolean
  buttonText?: string
  buttonClassName?: string
  editorClassName?: string
}

export const InlineEditor = ({
  value,
  onChange,
  onSubmit,
  onCancel,
  placeholder = 'Enter text...',
  className = 'text-label-default',
  maxLength = null,
  mode = 'view',
  variant = 'borderless',
  required = false,
  // Expandable input props
  expandable = false,
  buttonText = 'Add',
  buttonClassName = '',
  editorClassName = '',
  readOnly = false,
}: InlineEditorProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [isVisible, setIsVisible] = useState(!!value || !expandable)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    setIsEditing(true)
  }

  const handleSubmit = () => {
    setIsEditing(false)
    onSubmit?.()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSubmit()
    }
    if (e.key === 'Escape') {
      setIsEditing(false)
      onCancel?.()
    }
  }

  const handleBlur = () => {
    handleSubmit()
  }

  const handleButtonClick = () => {
    setIsVisible(true)
  }

  const renderInput = (
    <Input
      ref={inputRef}
      type={'text'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={cn('resize-none bg-transparent px-0', className, editorClassName, {
        'border-none outline-none': variant === 'borderless',
      })}
      placeholder={placeholder}
      maxLength={maxLength!}
    />
  )

  // Expandable input mode
  if (expandable && !isVisible) {
    return (
      <Button
        variant={'ghost-secondary'}
        onClick={handleButtonClick}
        className={cn(
          'flex items-center text-main-foreground-primary transition-all duration-150',
          buttonClassName,
        )}
      >
        <Plus size={12} strokeWidth={3} />
        <span className='text-main-foreground-tertiary'>{buttonText}</span>
      </Button>
    )
  }

  if (readOnly) {
    return <div className={cn('rounded py-1', className)}>{value || placeholder}</div>
  }

  if (isEditing) {
    return renderInput
  }

  return mode === 'edit' ? (
    renderInput
  ) : (
    <div
      onClick={handleClick}
      className={`${cn('group/inline-editor duration-150', { 'text-main-foreground-placeholder': !value })} cursor-text rounded ${className}`}
    >
      {value || placeholder}
      {required && <span className='text-main-foreground-danger'>*</span>}
      <PencilIcon className='ml-2 inline size-3 text-main-foreground-tertiary opacity-0 transition-opacity group-hover/inline-editor:opacity-100' />
    </div>
  )
}
